import {
  isToday,
  isYesterday,
  isWithinLastDays,
  isThisYear,
  Locale,
  getDateFormatter,
  TimeHoursMinutes,
  DayShort,
  DayLong,
  LongMonthDate,
  LongMonthDateNoYear,
  ShortMonthDate,
  ShortMonthDateNoYear
} from '@fluidx/utilities';

const oneMinute: number = 1000 * 60;
const oneHour: number = oneMinute * 60;
const justNowMinutesThreshold: number = 2;

export interface LastEditedDateProps {
  /**
   * The locale used for localizing strings.
   */
  locale: Locale;

  /**
   * The string table for more complex strings.
   */
  stringTable: LastEditedDateStringTable;

  /**
   * Whether or not to use long format for pre-generated localized dates. Usually using the `Intl.DateTimeFormat`
   */
  longFormat?: boolean;

  /**
   * If provided, it will be included in the returned string.
   */
  authorName?: string;
}

export interface LastEditedDateStringTable {
  relativeTimestampJustNow: string;
  relativeTimestampJustNowWithAuthor?: (placeholders: { authorName: string }) => string;
  relativeTimestampMinutesAgo: (placeholders: { minutes: number }) => string;
  relativeTimestampMinutesAgoWithAuthor?: (placeholders: { authorName: string; minutes: number }) => string;
  relativeTimestampHoursAgo: (placeholders: { hours: number }) => string;
  relativeTimestampHoursAgoWithAuthor?: (placeholders: { authorName: string; hours: number }) => string;
  relativeTimestampYesterday: (placeholders: { time: string }) => string;
  relativeTimestampYesterdayWithAuthor?: (placeholders: { authorName: string; time: string }) => string;
  relativeTimestampWithinLastDays: (placeholders: { day: string; time: string }) => string;
  relativeTimestampWithinLastDaysWithAuthor?: (placeholders: {
    authorName: string;
    day: string;
    time: string;
  }) => string;
  relativeTimestampGenericWithAuthor?: (placeholders: { authorName: string; date: string }) => string;
}

export function getLastEditedDateStringFromTimestamp(timestamp: number, props: LastEditedDateProps): string {
  const now = new Date(Date.now());
  const diffInMilliseconds = now.getTime() - timestamp;
  const diffInMinutes = Math.floor(diffInMilliseconds / oneMinute);

  const { locale, longFormat, stringTable, authorName } = props;

  if (diffInMinutes <= justNowMinutesThreshold) {
    if (authorName && stringTable.relativeTimestampJustNowWithAuthor) {
      return stringTable.relativeTimestampJustNowWithAuthor({ authorName });
    }
    return stringTable.relativeTimestampJustNow;
  }

  if (diffInMinutes < 60) {
    if (authorName && stringTable.relativeTimestampMinutesAgoWithAuthor) {
      return stringTable.relativeTimestampMinutesAgoWithAuthor({ minutes: diffInMinutes, authorName });
    }
    return stringTable.relativeTimestampMinutesAgo({ minutes: diffInMinutes });
  }

  const date = new Date(timestamp);
  if (isToday(date)) {
    const diffInHours = Math.floor(diffInMilliseconds / oneHour);
    if (authorName && stringTable.relativeTimestampHoursAgoWithAuthor) {
      return stringTable.relativeTimestampHoursAgoWithAuthor({ hours: diffInHours, authorName });
    }
    return stringTable.relativeTimestampHoursAgo({ hours: diffInHours });
  }

  const timeFormatter = getDateFormatter(locale, TimeHoursMinutes);
  const time = timeFormatter.format(date);

  if (isYesterday(date)) {
    if (authorName && stringTable.relativeTimestampYesterdayWithAuthor) {
      return stringTable.relativeTimestampYesterdayWithAuthor({ time, authorName });
    }
    return stringTable.relativeTimestampYesterday({ time });
  }

  if (isWithinLastDays(date, 7)) {
    const dateFormatter = getDateFormatter(locale, longFormat ? DayLong : DayShort);
    const day = dateFormatter.format(date);

    if (authorName && stringTable.relativeTimestampWithinLastDaysWithAuthor) {
      return stringTable.relativeTimestampWithinLastDaysWithAuthor({ day, time, authorName });
    }
    return stringTable.relativeTimestampWithinLastDays({ day, time });
  }

  const dateFormat = longFormat
    ? isThisYear(date)
      ? LongMonthDateNoYear
      : LongMonthDate
    : isThisYear(date)
    ? ShortMonthDateNoYear
    : ShortMonthDate;

  const dateFormatter = getDateFormatter(locale, dateFormat);
  const dateWithLocale = dateFormatter.format(date);

  if (authorName && stringTable.relativeTimestampGenericWithAuthor) {
    return stringTable.relativeTimestampGenericWithAuthor({ date: dateWithLocale, authorName });
  }

  return dateWithLocale;
}
